import { ProductType } from "app/shared/model";
import { ProductFilterDto } from "app/shared/model/DiscountDto";

export enum ProductFilterType {
    AnyProduct,
    ByType,
    Homestay,
    Residence,
    IncludeSpecific,
    ExcludeSpecific
}

export type AnyProductFilter = {
    type: ProductFilterType.AnyProduct,
}

export type ByProductTypeFilter = {
    type: ProductFilterType.ByType
    productType: ProductType
}

export type SpecificProductsFilter = {
    type: ProductFilterType.IncludeSpecific|ProductFilterType.ExcludeSpecific,
    productType: ProductType,
    productCodes: SelectionMap
}

export type ResidenceTypeFilter = {
    type: ProductFilterType.Residence|ProductFilterType.Homestay,
    productCodes?: SelectionMap
}

export type ProductFilter = 
    AnyProductFilter
    | ByProductTypeFilter
    | ResidenceTypeFilter
    | SpecificProductsFilter

export type SelectionMap = {
    [code: string]: true|undefined
}

export function filterNeedsProductCodes(productFilterType: ProductFilterType) {
    return (productFilterType !== ProductFilterType.AnyProduct &&
        productFilterType !== ProductFilterType.ByType &&
        productFilterType !== ProductFilterType.Homestay &&
        productFilterType !== ProductFilterType.Residence) ;
}

export function getProductType(filter: ProductFilter) {
    switch(filter.type) {
        case ProductFilterType.AnyProduct:
            return undefined;

        case ProductFilterType.Homestay:
        case ProductFilterType.Residence:
            return ProductType.Accommodation;

        case ProductFilterType.ByType:
        case ProductFilterType.IncludeSpecific:
        case ProductFilterType.ExcludeSpecific:
            return filter.productType;

        default:
            throw new Error('Unknown product selection filter type');
    }
}

export function getSelectedProductCodes(filter: ProductFilter) : SelectionMap {
    switch(filter.type) {
        case ProductFilterType.AnyProduct:
        case ProductFilterType.ByType:
            return {};

        case ProductFilterType.Homestay:
        case ProductFilterType.Residence:
            return filter.productCodes || {};

        case ProductFilterType.IncludeSpecific:
        case ProductFilterType.ExcludeSpecific:
            return filter.productCodes;

        default:
            throw new Error('Unknown product selection filter type')
    }
}

export function parseProductFilterDto(dto: ProductFilterDto) {
    let newProductFilter : ProductFilter;
    switch (dto.filterType) {
        case ProductFilterType.ByType:
            newProductFilter = {
                type: ProductFilterType.ByType,
                productType: dto.productType||ProductType.Course
            };
            break;
        case ProductFilterType.Homestay:
        case ProductFilterType.Residence:
            if (dto.codes && dto.codes.length > 0) {
                const productCodes : SelectionMap = {};
                dto.codes.forEach(c => productCodes[c] = true);
                newProductFilter = {
                    type: dto.filterType,
                    productCodes: productCodes
                };
            } else {
                newProductFilter = {
                    type: dto.filterType
                };
            }
            break;
        case ProductFilterType.ExcludeSpecific:
        case ProductFilterType.IncludeSpecific:
            const productCodes : SelectionMap = {};
            if (dto.codes) {
                dto.codes.forEach(c => productCodes[c] = true);
            }
            newProductFilter = {
                type: dto.filterType,
                productType: dto.productType||ProductType.Course,
                productCodes: productCodes
            };
            break;
        case ProductFilterType.AnyProduct:
        default:
            newProductFilter = {type: ProductFilterType.AnyProduct};
            break;
    }

    return newProductFilter;
}

export function productFilterToDto(filter: ProductFilter | undefined | null) : ProductFilterDto {
    if (!filter) {
        return {
            filterType: ProductFilterType.AnyProduct
        };
    }
    
    let codes : string[] = [];
    let productType : ProductType|undefined = undefined;

    switch(filter.type) {
        case ProductFilterType.AnyProduct:
            break;
        case ProductFilterType.ByType:
            productType = filter.productType;
            break;
        case ProductFilterType.Homestay:
        case ProductFilterType.Residence:
            if ((filter as any).productCodes && Object.keys((filter as any).productCodes).length > 0) {
                codes = Object.keys((filter as any).productCodes).filter(k => (filter as any).productCodes[k]);
            }
            const result: ProductFilterDto = {
                filterType: filter.type,
                productType: ProductType.Accommodation
            };
            if (codes.length > 0) {
                result.codes = codes;
            }
            return result;
        case ProductFilterType.ExcludeSpecific:
        case ProductFilterType.IncludeSpecific:
            productType =  filter.productType;
            if (filter.productCodes) {
                const selectedCodes = Object.keys(filter.productCodes).filter(k => filter.productCodes![k]);
                codes = productType
                    ? selectedCodes.filter(c => c.startsWith(productType + '-'))
                    : selectedCodes;
            } else {
                codes = [];
            }
            
            if (filter.type === ProductFilterType.IncludeSpecific && codes.length === 0 && productType) {
                return {
                    filterType: ProductFilterType.ByType,
                    productType: productType
                };
            }
            return {
                filterType: filter.type,
                productType: productType,
                codes: codes
            };
        default:
            throw new Error('Unknown product filter type');
    }

    const result: ProductFilterDto = {
        filterType: filter.type
    };
    if (productType !== undefined && productType !== null) {
        result.productType = productType;
    }
    return result;
}

export function setProductFilterType(state: ProductFilter, filterType: ProductFilterType) : ProductFilter {
    if (state.type == filterType)
        return state;

    if (filterType == ProductFilterType.AnyProduct)
        return  {type: ProductFilterType.AnyProduct};

    if (filterType == ProductFilterType.Homestay || filterType == ProductFilterType.Residence) {
        return {type: filterType}
    }

    const productType = state.type == ProductFilterType.ByType 
                || state.type == ProductFilterType.ExcludeSpecific 
                || state.type == ProductFilterType.IncludeSpecific
        ? state.productType
        : ProductType.Course;

    if (filterType == ProductFilterType.ByType)
        return {type: ProductFilterType.ByType, productType: productType}

    const currentCodes = state.type == ProductFilterType.IncludeSpecific
        || state.type == ProductFilterType.ExcludeSpecific
        ? state.productCodes
        : {};

    return {
        type: filterType,
        productType: productType,
        productCodes: currentCodes
    }
}

function codeMatchesProductType(code: string, productType: ProductType): boolean {
    return code.startsWith(productType + '-');
}

export function setProductType(state: ProductFilter, productType: ProductType) : ProductFilter {
    if ((state.type == ProductFilterType.IncludeSpecific ||
        state.type == ProductFilterType.ExcludeSpecific ||
        state.type == ProductFilterType.ByType) &&
        state.productType == productType) {
        return state;
    }

    const filterType = state.type == ProductFilterType.AnyProduct || state.type == ProductFilterType.Homestay ||
                            state.type == ProductFilterType.Residence
        ? ProductFilterType.ByType
        : state.type;

    let currentCodes: SelectionMap = {};
    if (state.type == ProductFilterType.IncludeSpecific || state.type == ProductFilterType.ExcludeSpecific) {
        const prevCodes = state.productCodes || {};
        Object.keys(prevCodes).forEach(k => {
            if (prevCodes[k] && codeMatchesProductType(k, productType)) {
                currentCodes[k] = true;
            }
        });
    }

    if (filterType == ProductFilterType.ByType)
        return {type: filterType, productType: productType}

    return {type: filterType, productType: productType, productCodes: currentCodes}
}

export function changeProductSelection(filter: ProductFilter, code: string, isSelected: boolean) : ProductFilter {
    if (filter.type == ProductFilterType.ByType) {
        if (!isSelected) {
            return filter;
        }
        const productType = filter.productType;
        if (productType === undefined) {
            return filter;
        }
        return {
            type: ProductFilterType.IncludeSpecific,
            productType: productType,
            productCodes: { [code]: true }
        };
    }

    if (filter.type == ProductFilterType.Homestay || filter.type == ProductFilterType.Residence) {
        const currentCodes = filter.productCodes || {};
        if (!isSelected) {
            const newCodes = { ...currentCodes };
            delete newCodes[code];
            return {
                ...filter,
                productCodes: Object.keys(newCodes).length > 0 ? newCodes : undefined
            };
        }
        return {
            ...filter,
            productCodes: { ...currentCodes, [code]: true }
        };
    }
    if (filter.type == ProductFilterType.IncludeSpecific || filter.type == ProductFilterType.ExcludeSpecific) {
        const currentProductCodes = filter.productCodes || {};
        const isAlreadySelected = !!currentProductCodes[code];
        if (isAlreadySelected == isSelected)
            return filter;
        
        const newProductCodes = isSelected
            ? addSelection(currentProductCodes, code)
            : removeSelection(currentProductCodes, code);
        
        if (filter.type === ProductFilterType.IncludeSpecific && Object.keys(newProductCodes).length === 0 && filter.productType) {
            return {
                type: ProductFilterType.ByType,
                productType: filter.productType
            };
        }
        
        return {
            ... filter,
            productCodes: newProductCodes
        }
    }

    return filter;
}

function addSelection(selectionMap: SelectionMap, code: string) : SelectionMap {
    if (selectionMap[code])
        return selectionMap;

    return {
        ... selectionMap,
        [code]: true
    }
}

function removeSelection(selectionMap: SelectionMap, code: string) : SelectionMap {
    const newCodes : SelectionMap = { };
    Object.keys(selectionMap).forEach(c => {
        if (c !== code)
            newCodes[c] = true;
    });

    return newCodes;
}